import React, { useRef, useState, useLayoutEffect } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { useGlobalTheme } from '../../hooks/useGlobalTheme';
import { isSaudiBrand } from '../../../products/saudi/utils/isSaudiBrand';

/**
 * CustomNumberInput - GPS-style number input with up/down arrows
 *
 * @param {string} props.suffixInText - Currency/unit shown live after digits (e.g. ' DA') — not typed by user
 */
const CustomNumberInput = ({
  value,
  onChange,
  placeholder = '',
  className = '',
  disabled = false,
  step = 1,
  min,
  max,
  decimals,
  required = false,
  suffix = '',
  suffixInText = '',
  'data-testid': dataTestId,
}) => {
  const { isDashboardLightMode: isLight } = useGlobalTheme();
  const qawaem = isSaudiBrand();
  const inputRef = useRef(null);
  const measureRef = useRef(null);
  const [suffixLeft, setSuffixLeft] = useState(16);

  const formatNumericValue = (val) => {
    if (val === '' || val === null || val === undefined) return '';
    const s = String(val).trim().replace(/\s/g, '').replace(',', '.');
    if (s === '') return '';
    const num = parseFloat(s);
    if (Number.isNaN(num)) return '';
    if (decimals !== undefined) {
      if (Number.isInteger(num)) return String(num);
      return Number(num).toFixed(decimals);
    }
    return String(num);
  };

  const numericDisplay = formatNumericValue(value);
  const showLiveSuffix = Boolean(suffixInText && numericDisplay);

  useLayoutEffect(() => {
    if (!suffixInText || !showLiveSuffix) return;
    const inputEl = inputRef.current;
    const measureEl = measureRef.current;
    if (!inputEl || !measureEl) return;

    const style = window.getComputedStyle(inputEl);
    measureEl.style.font = style.font;
    measureEl.style.fontSize = style.fontSize;
    measureEl.style.fontWeight = style.fontWeight;
    measureEl.style.fontFamily = style.fontFamily;
    measureEl.style.letterSpacing = style.letterSpacing;
    measureEl.textContent = numericDisplay;
    const textWidth = measureEl.offsetWidth;
    const paddingLeft = parseFloat(style.paddingLeft) || 16;
    setSuffixLeft(paddingLeft + textWidth + 1);
  }, [numericDisplay, suffixInText, showLiveSuffix]);

  const handleIncrement = () => {
    if (disabled) return;
    let newValue = (parseFloat(value) || 0) + step;
    if (max !== undefined && newValue > max) newValue = max;
    if (decimals !== undefined) newValue = parseFloat(newValue.toFixed(decimals));
    onChange(newValue);
  };

  const handleDecrement = () => {
    if (disabled) return;
    let newValue = (parseFloat(value) || 0) - step;
    if (min !== undefined && newValue < min) newValue = min;
    if (decimals !== undefined) newValue = parseFloat(newValue.toFixed(decimals));
    onChange(newValue);
  };

  const handleInputChange = (e) => {
    const inputValue = e.target.value.replace(/\s/g, '').replace(',', '.');

    if (inputValue === '') {
      onChange('');
      return;
    }

    const numValue = parseFloat(inputValue);
    if (!Number.isNaN(numValue)) {
      onChange(numValue);
    }
  };

  return (
    <div className={`relative ${className}`} data-testid={dataTestId}>
      <span
        ref={measureRef}
        aria-hidden
        className="pointer-events-none absolute invisible whitespace-pre"
        style={{ top: 0, left: 0 }}
      />

      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          required={required}
          value={numericDisplay}
          onChange={handleInputChange}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="off"
          className={`
            w-full rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 transition-all
            ${qawaem ? 'focus:ring-[#6e9b6e]/45' : 'focus:ring-blue-500'}
            ${isLight
              ? 'bg-white border border-slate-300 text-slate-900 placeholder-slate-400'
              : 'bg-white/10 border border-white/20 text-white placeholder-white/30'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            ${suffixInText ? 'pr-16' : 'pr-10'}
          `}
        />

        {suffix && !suffixInText && value && (
          <span
            className={`absolute pointer-events-none ${isLight ? 'text-slate-700' : 'text-white'}`}
            style={{
              left: `calc(1rem + ${String(value).length * 0.6}ch + 4px)`,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '16px',
              lineHeight: '1',
            }}
          >
            {suffix}
          </span>
        )}

        {showLiveSuffix && (
          <span
            className={`pointer-events-none absolute select-none ${isLight ? 'text-slate-600' : 'text-white/90'}`}
            style={{
              left: `${suffixLeft}px`,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '16px',
              lineHeight: '1',
            }}
          >
            {'\u00a0'}
            {suffixInText.trim()}
          </span>
        )}
      </div>

      <div
        className={`qawaem-number-stepper absolute right-0 top-0 bottom-0 flex flex-col w-8 border-l rounded-r-xl overflow-hidden ${
          isLight ? 'border-slate-300' : 'border-white/20'
        }`}
      >
        <button
          type="button"
          onClick={handleIncrement}
          disabled={disabled || (max !== undefined && parseFloat(value) >= max)}
          className={`qawaem-number-stepper-btn qawaem-number-stepper-btn--up flex-1 flex items-center justify-center transition-all group disabled:opacity-50 disabled:cursor-not-allowed ${
            qawaem
              ? isLight
                ? 'bg-[#f4f7f4] hover:bg-[#6e9b6e]/15'
                : 'bg-[#2e452e]/45 hover:bg-[#6e9b6e]/55'
              : isLight
                ? 'bg-slate-100 hover:bg-blue-100'
                : 'bg-blue-900/60 hover:bg-blue-600/80'
          }`}
        >
          <ChevronUp
            size={14}
            className={`transition-colors ${
              qawaem
                ? isLight
                  ? 'text-[#475947] group-hover:text-[#2e452e]'
                  : 'text-[#a8c4a8] group-hover:text-white'
                : isLight
                  ? 'text-slate-600 group-hover:text-blue-700'
                  : 'text-blue-300 group-hover:text-white'
            }`}
          />
        </button>

        <div
          className={`h-px ${
            qawaem
              ? isLight
                ? 'bg-[#6e9b6e]/20'
                : 'bg-[#6e9b6e]/30'
              : isLight
                ? 'bg-slate-200'
                : 'bg-blue-400/30'
          }`}
        />

        <button
          type="button"
          onClick={handleDecrement}
          disabled={disabled || (min !== undefined && parseFloat(value) <= min)}
          className={`qawaem-number-stepper-btn qawaem-number-stepper-btn--down flex-1 flex items-center justify-center transition-all group disabled:opacity-50 disabled:cursor-not-allowed ${
            qawaem
              ? isLight
                ? 'bg-[#f4f7f4] hover:bg-[#6e9b6e]/15'
                : 'bg-[#2e452e]/45 hover:bg-[#6e9b6e]/55'
              : isLight
                ? 'bg-slate-100 hover:bg-blue-100'
                : 'bg-blue-900/60 hover:bg-blue-600/80'
          }`}
        >
          <ChevronDown
            size={14}
            className={`transition-colors ${
              qawaem
                ? isLight
                  ? 'text-[#475947] group-hover:text-[#2e452e]'
                  : 'text-[#a8c4a8] group-hover:text-white'
                : isLight
                  ? 'text-slate-600 group-hover:text-blue-700'
                  : 'text-blue-300 group-hover:text-white'
            }`}
          />
        </button>
      </div>
    </div>
  );
};

export default CustomNumberInput;
